import React, { useState, useEffect, useRef, useCallback } from "react";
import RotationController from "./RotationController";
import Legend from "./Legend";
import PinLayer from "./PinLayer";
import useAutoScale from "../hooks/useAutoScale";
import usePreventDoubleTapZoom from "../hooks/usePreventDoubleTapZoom";
import { usePins } from "../contexts/PinContext";
import { CoordinateConverter } from "../utils";
import { useTapDetection } from "../hooks/useTapDetection";
import { PinType } from "../types";
import { UI_CONFIG, PIN_COLORS } from "../constants";

interface MapProps {
  mapSrc: string;
}

/**
 * マップ表示コンポーネント
 * ピンの管理と ユーザーインタラクションを処理
 */
const Map: React.FC<MapProps> = ({ mapSrc }) => {
  const { pins, addPin, removePin } = usePins();
  const [rotation, setRotationState] = useState<number>(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);
  const scale = useAutoScale(containerRef, imgRef, rotation);
  const rotationRef = useRef(rotation);

  usePreventDoubleTapZoom();

  // 回転値を ref に同期
  useEffect(() => {
    rotationRef.current = rotation;
  }, [rotation]);

  // 回転を相対的に更新
  const rotateBy = useCallback((degrees: number) => {
    setRotationState((r) => {
      let normalized = (r + degrees) % 360;
      return normalized < 0 ? normalized + 360 : normalized;
    });
  }, []);

  // タップ detection
  const { handlePointerDown, handlePointerMove, handlePointerUp, dispose } = useTapDetection(
    useCallback(
      (e: React.PointerEvent<HTMLDivElement>) =>
        CoordinateConverter.clientToRatio(
          e.currentTarget as HTMLElement,
          e.clientX,
          e.clientY,
          rotationRef.current,
          scale
        ),
      [scale]
    ),
    useCallback(
      (type: any, coordinates: any) => {
        const pinType =
          type === "double"
            ? PinType.Enemy
            : type === "long"
              ? PinType.General
              : PinType.Ally;
        addPin(coordinates.xRatio, coordinates.yRatio, pinType, mapSrc);
      },
      [addPin, mapSrc]
    )
  );

  useEffect(() => {
    return dispose;
  }, [dispose]);

  return (
    <div
      ref={containerRef}
      style={{
        position: "relative",
        width: "100%",
        height: "100vh",
        touchAction: "manipulation",
        overflow: "hidden",
      }}
    >
      <RotationController
        onChange={rotateBy}
        style={{
          position: "absolute",
          top: 24,
          left: 8,
          zIndex: UI_CONFIG.Z_INDEX_LEGEND_ROTATION,
        }}
      />

      <Legend
        items={[
          { color: PIN_COLORS[PinType.Ally], label: "味方: タップ" },
          { color: PIN_COLORS[PinType.Enemy], label: "敵: ダブルタップ" },
          { color: PIN_COLORS[PinType.General], label: "汎用: 長押し" },
        ]}
        style={{
          position: "absolute",
          top: 8,
          right: 8,
          zIndex: UI_CONFIG.Z_INDEX_LEGEND_ROTATION,
        }}
      />

      {/* 回転＋縮小をかけるラッパー。中心で回転・拡縮 */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: `translate(-50%,-50%) rotate(${rotation}deg) scale(${scale})`,
          transformOrigin: "center center",
          display: "inline-block",
          zIndex: UI_CONFIG.Z_INDEX_MAP_LAYER,
          pointerEvents: "auto",
        }}
      >
        <img
          ref={imgRef}
          src={mapSrc}
          alt="Map"
          style={{
            width: "auto",
            height: "auto",
            display: "block",
            objectFit: "contain",
            pointerEvents: "none",
            userSelect: "none",
            WebkitUserSelect: "none",
          }}
        />
        <PinLayer pins={pins} rotation={rotation} onRemovePin={removePin} />
      </div>
    </div>
  );
};

export default Map;
